import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import urls from '../../../../networkRequests/apiClient/endpoints'
import errorHandler from '../../../../shared/functions/ErrorHandler/errorHandler.functions'
import { ApiErrorResponse } from '../../../../shared/types/api.types'
import { getPurchaseReadyItemsApi } from './getPurchaseReadyItems.api'
import getPurchaseReadyItemsInitialState, {
  emptyPurchaseReadyItemsStatusState
} from './getPurchaseReadyItems.initialState'
import {
  GetPurchaseReadyItemsErrorResponse,
  GetPurchaseReadyItemsRequestPayload,
  GetPurchaseReadyItemsState,
  GetPurchaseReadyItemsStatusState
} from './getPurchaseReadyItems.types'

export const getPurchaseReadyItems = createAsyncThunk(
  urls.admin.getPurchaseReadyItems,
  async (payload: GetPurchaseReadyItemsRequestPayload, { rejectWithValue }) => {
    const { request, onSuccess } = payload

    try {
      const response = await getPurchaseReadyItemsApi(request!)

      onSuccess && onSuccess()

      return response
    } catch (error) {
      errorHandler.handleApiError(error as ApiErrorResponse)

      return rejectWithValue(error)
    }
  }
)

/**
 * The pigeonhole a request's result belongs in — the `status` it queried. Read
 * off `meta.arg`, which the thunk carries through pending/fulfilled/rejected, so
 * every call site keys itself correctly without having to remember to.
 *
 * A request with no `status` is the whole unfiltered list; it gets its own key
 * rather than being merged into any tab's.
 */
const statusKeyOf = (arg: GetPurchaseReadyItemsRequestPayload): string => arg?.request?.status ?? ''

const stateForStatus = (state: GetPurchaseReadyItemsState, key: string): GetPurchaseReadyItemsStatusState => {
  if (!state.byStatus[key]) {
    state.byStatus[key] = { ...emptyPurchaseReadyItemsStatusState }
  }

  return state.byStatus[key]
}

const getPurchaseReadyItemsSlice = createSlice({
  name: urls.admin.getPurchaseReadyItems,
  initialState: getPurchaseReadyItemsInitialState,
  reducers: {
    resetGetPurchaseReadyItems: (state) => {
      state.byStatus = {}
    }

    // Add any additional reducers here
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPurchaseReadyItems.pending, (state, { meta }) => {
        const entry = stateForStatus(state, statusKeyOf(meta.arg))

        entry.loading = true
        entry.error = null
        entry.success = null
      })
      .addCase(getPurchaseReadyItems.fulfilled, (state, { payload, meta }) => {
        const entry = stateForStatus(state, statusKeyOf(meta.arg))

        entry.loading = false
        entry.success = payload
        entry.error = null
      })
      .addCase(getPurchaseReadyItems.rejected, (state, { payload, meta }) => {
        const entry = stateForStatus(state, statusKeyOf(meta.arg))

        entry.loading = false
        entry.error = payload as GetPurchaseReadyItemsErrorResponse
        entry.success = null
      })
  }
})

/**
 * Read one status's slice of the list. Always select through this: reaching into
 * `byStatus` inline risks a caller keying its read differently from its write,
 * which is the failure this structure exists to prevent.
 */
export const selectPurchaseReadyItemsForStatus =
  (status: string) =>
  (state: { getPurchaseReadyItems: GetPurchaseReadyItemsState }): GetPurchaseReadyItemsStatusState =>
    state.getPurchaseReadyItems.byStatus[status] ?? emptyPurchaseReadyItemsStatusState

export const { actions: getPurchaseReadyItemsActions, reducer: getPurchaseReadyItemsReducer } =
  getPurchaseReadyItemsSlice
export const { resetGetPurchaseReadyItems } = getPurchaseReadyItemsActions
export default getPurchaseReadyItemsSlice.reducer
